/**
 * ReceiptPreview — live thermal-receipt preview for the billing screen.
 *
 * Renders the SAME text `buildReceipt()` produces for the printer, sourced from
 * the live cart via `cartToReceipt`, so what the operator sees updates as items
 * are added and matches the eventual printout (fewer print surprises).
 */
import { useMemo } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Receipt } from 'lucide-react';
import { database } from '@/db';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import { useObservable } from '@/hooks/useObservable';
import { useAppStore } from '@/store/useAppStore';
import { useCartStore } from '@/store/useCartStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { buildReceipt } from '@/printing/engine/ReceiptBuilder';
import { resolveReceiptConfig } from '@/printing/engine/receiptConfig';
import type { PaperWidth } from '@/printing/contracts/PrintTypes';
import { cartToReceipt } from '@/printing/adapters/cartToReceipt';
import { cn } from '@/utils/cn';

export function ReceiptPreview({ className }: { className?: string }) {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const adjustedItems = useCartStore((s) => s.adjustedItems);
  const totals = useCartStore((s) => s.totals);
  const customer = useCartStore((s) => s.customer);
  const payments = useCartStore((s) => s.payments);
  const itemCount = useCartStore((s) => s.itemCount);
  // Same paper-width source as the real print path (useInvoicePrint), so the
  // preview column matches the printed width.
  const paperWidth: PaperWidth = useSettingsStore((s) => {
    const raw = s.getNumber('THERMAL_PRINTER_PAPER_WIDTH');
    return raw === 58 || raw === 112 ? raw : 80;
  });
  // Re-resolve whenever RECEIPT_CONFIG changes so preview tracks the designer.
  const configRaw = useSettingsStore((s) => s.getSetting('RECEIPT_CONFIG'));
  const config = useMemo(() => resolveReceiptConfig(useSettingsStore.getState()), [configRaw]);

  const stores = useObservable<Store[]>(
    () =>
      activeStoreId
        ? database.get<Store>('stores').query(Q.where('id', activeStoreId)).observe()
        : null,
    [activeStoreId],
    [],
  );
  const store = stores[0];

  /**
   * The merchant behind the store, for the GSTIN fallback below.
   *
   * Observed rather than fetched once, so editing the GSTIN in Settings
   * re-renders the preview the same way editing the address already does.
   */
  const merchants = useObservable<Merchant[]>(
    () =>
      store?.merchantId
        ? database.get<Merchant>('merchants').query(Q.where('id', store.merchantId)).observe()
        : null,
    [store?.merchantId],
    [],
  );
  const merchant = merchants[0];

  const text = useMemo(() => {
    if (!store || itemCount === 0) return '';
    const data = cartToReceipt({
      items: adjustedItems,
      totals,
      customer: customer ? { name: customer.name, phone: customer.phone } : null,
      payments: payments.map((p) => ({ method: p.method, amount: p.amount })),
      store: {
        name: store.name,
        address: store.address,
        city: store.city,
        state: store.state,
        pincode: store.pincode,
        // Store override first, then the business GSTIN.
        //
        // This read `store.gstinOverride` alone, so a GST-registered merchant
        // who never filled the optional STORE-level field printed bills with no
        // GSTIN at all — which Rule 46 requires on a tax invoice. The field is
        // even labelled "overrides the business GSTIN", promising exactly this
        // fallback. Every other consumer already resolves it this way:
        // storeIdentity, the e-way bill source, and the dashboard's own "GST
        // configured" tick — which is why the checklist said done while the
        // customer's copy said nothing.
        gstin: store.gstinOverride || merchant?.gstin || null,
        phone: store.phone,
      },
    });
    return buildReceipt(data, { paperWidth, autoCut: false }, config).text;
  }, [store, merchant?.gstin, itemCount, adjustedItems, totals, customer, payments, paperWidth, config]);

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-lg border border-stroke-hairline/10 bg-bg-subtle',
        className,
      )}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-stroke-hairline/10 px-4 py-3">
        <Receipt size={16} className="text-fg-secondary" />
        <span className="text-label uppercase tracking-wide text-fg-secondary">Receipt preview</span>
        <span className="ml-auto text-label text-fg-tertiary">{paperWidth}mm</span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {itemCount === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-fg-tertiary">
            <Receipt size={22} />
            <p className="text-body">Add items to preview the receipt.</p>
          </div>
        ) : (
          <pre className="mx-auto w-fit whitespace-pre rounded-sm bg-white px-3 py-4 font-mono text-[11px] leading-[1.45] text-neutral-800 shadow-e1">
            {text}
          </pre>
        )}
      </div>
    </div>
  );
}
